// src/components/padrekko/PadAccessRoadLayer.jsx
//
// Renders the access-road selection list for the currently-selected pad.
// Draws both detected and user-drawn roads, with two visual states:
//
//   included = true   → warm orange dashed stroke with dark casing (active)
//   included = false  → dimmed grey dashed stroke (candidate, excluded)
//
// Click on a road → store.toggleRoadInclusion + store.pulseRoad(key) so the
// panel chip briefly highlights. Click is suppressed while drawing mode is
// active (MlDraw owns the map click in that case).

import { useEffect, useRef } from "react";
import { usePadStore } from "../../stores/usePadStore";

const SOURCE_ID = "pad-access-roads";
const LAYERS = {
  CASING_IN:  "pad-access-roads-casing-in",
  CASING_OUT: "pad-access-roads-casing-out",
  LINE_IN:    "pad-access-roads-line-in",
  LINE_OUT:   "pad-access-roads-line-out",
  HITBOX:     "pad-access-roads-hitbox",
};

const EMPTY_FC = { type: "FeatureCollection", features: [] };

export default function PadAccessRoadLayer({ map, addLayer }) {

  const roadSelections  = usePadStore(s => s.roadSelections);
  const drawingRoad     = usePadStore(s => s.drawingRoad);
  const toggleInclusion = usePadStore(s => s.toggleRoadInclusion);
  const pulseRoad       = usePadStore(s => s.pulseRoad);
  const initRef = useRef(false);

  /* ---------------- SETUP ---------------- */

  useEffect(() => {
    if (!map) return;

    const setup = () => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, { type: "geojson", data: EMPTY_FC });
      }

      // INCLUDED — casing
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.CASING_IN,
          type: "line",
          source: SOURCE_ID,
          filter: ["==", ["get", "included"], true],
          layout: { "line-cap": "round", "line-join": "round", visibility: "visible" },
          paint: {
            "line-color":   "#0f172a",
            "line-width":   ["interpolate", ["linear"], ["zoom"], 10, 3,  15, 6,  18, 9],
            "line-opacity": 0.85,
          },
        },
      });

      // INCLUDED — orange stroke
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.LINE_IN,
          type: "line",
          source: SOURCE_ID,
          filter: ["==", ["get", "included"], true],
          layout: { "line-cap": "round", "line-join": "round", visibility: "visible" },
          paint: {
            "line-color":     "#fda261",
            "line-width":     ["interpolate", ["linear"], ["zoom"], 10, 1.5, 15, 3.5, 18, 5.5],
            "line-opacity":   1,
            "line-dasharray": [2, 1],
          },
        },
      });

      // EXCLUDED — dimmer casing
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.CASING_OUT,
          type: "line",
          source: SOURCE_ID,
          filter: ["==", ["get", "included"], false],
          layout: { "line-cap": "round", "line-join": "round", visibility: "visible" },
          paint: {
            "line-color":   "#0f172a",
            "line-width":   ["interpolate", ["linear"], ["zoom"], 10, 2,  15, 4,  18, 6],
            "line-opacity": 0.4,
          },
        },
      });

      // EXCLUDED — grey dashed
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.LINE_OUT,
          type: "line",
          source: SOURCE_ID,
          filter: ["==", ["get", "included"], false],
          layout: { "line-cap": "round", "line-join": "round", visibility: "visible" },
          paint: {
            "line-color":     "#64748b",  // slate-500
            "line-width":     ["interpolate", ["linear"], ["zoom"], 10, 1, 15, 2, 18, 3],
            "line-opacity":   0.65,
            "line-dasharray": [1, 2],
          },
        },
      });

      // HITBOX — invisible wide line on top for reliable clicks
      addLayer({
        group: "asset",
        layerConfig: {
          id: LAYERS.HITBOX,
          type: "line",
          source: SOURCE_ID,
          layout: { visibility: "visible" },
          paint: {
            "line-color":   "#000000",
            "line-width":   14,
            "line-opacity": 0,   // invisible but still hit-tested
          },
        },
      });

      initRef.current = true;
    };

    if (map.isStyleLoaded()) setup();
    else map.once("idle", setup);

    return () => {
      if (!map?.getStyle?.()) { initRef.current = false; return; }
      Object.values(LAYERS).forEach((id) => {
        try { if (map.getLayer(id)) map.removeLayer(id); } catch (_) {}
      });
      try { if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID); } catch (_) {}
      initRef.current = false;
    };
  }, [map, addLayer]);

  /* ---------------- SYNC DATA ---------------- */

  useEffect(() => {
    if (!map || !map.getSource(SOURCE_ID)) return;

    const fc = {
      type: "FeatureCollection",
      features: roadSelections
        .filter((r) => r.geom)
        .map((r) => ({
          type: "Feature",
          geometry: r.geom,
          properties: {
            key: r.key,
            source: r.source,
            included: r.included,
            length_m: r.length_m,
          },
        })),
    };

    map.getSource(SOURCE_ID).setData(fc);
  }, [map, roadSelections]);

  /* ---------------- INTERACTION ---------------- */

  useEffect(() => {
    if (!map) return;

    const handleClick = (e) => {
      if (usePadStore.getState().drawingRoad) return;
      const f = e.features?.[0];
      const key = f?.properties?.key;
      if (!key) return;

      // Stop propagation so pad-clear doesn't also fire on empty-map
      e.originalEvent._padRoadHandled = true;

      toggleInclusion(key);
      pulseRoad(key);
    };

    const handleEnter = () => {
      if (usePadStore.getState().drawingRoad) return;
      map.getCanvas().style.cursor = "pointer";
    };

    const handleLeave = () => {
      if (usePadStore.getState().drawingRoad) return;
      map.getCanvas().style.cursor = "";
    };

    map.on("click",      LAYERS.HITBOX, handleClick);
    map.on("mouseenter", LAYERS.HITBOX, handleEnter);
    map.on("mouseleave", LAYERS.HITBOX, handleLeave);

    return () => {
      map.off("click",      LAYERS.HITBOX, handleClick);
      map.off("mouseenter", LAYERS.HITBOX, handleEnter);
      map.off("mouseleave", LAYERS.HITBOX, handleLeave);
    };
  }, [map, toggleInclusion, pulseRoad]);

  return null;
}

